<template>
  <div 
    class="callback-form"
    :style="{
      border: '1px solid #d1d5db',
      borderRadius: '0.5rem',
      backgroundColor: 'white'
    }"
  >
    <div class="flex flex-col p-2">
      <h3 class="text-sm font-semibold text-neutral-500 mb-2">Rückruf anfordern</h3>
      
      <form @submit.prevent="requestCallback" class="space-y-3">
        <div>
          <label for="callbackName" class="block text-xs font-medium text-neutral-600 mb-1">Name</label>
          <input 
            type="text" 
            id="callbackName" 
            v-model="form.name" 
            class="w-full bg-gray-50 px-3 py-2 text-xs text-gray-700 rounded-md"
            :style="{
              border: '1px solid #e5e7eb'
            }"
            required
          />
        </div>
        
        <div>
          <label for="callbackPhone" class="block text-xs font-medium text-neutral-600 mb-1">Telefonnummer</label>
          <input 
            type="tel" 
            id="callbackPhone" 
            v-model="form.phone" 
            class="w-full bg-gray-50 px-3 py-2 text-xs text-gray-700 rounded-md"
            :style="{
              border: '1px solid #e5e7eb'
            }"
            required
          />
        </div>
        
        <div>
          <label for="callbackEmail" class="block text-xs font-medium text-neutral-600 mb-1">Email</label>
          <input 
            type="email" 
            id="callbackEmail" 
            v-model="form.email" 
            class="w-full bg-gray-50 px-3 py-2 text-xs text-gray-700 rounded-md"
            :style="{
              border: '1px solid #e5e7eb'
            }"
            required
          />
        </div>
        
        <div>
          <label for="callbackTime" class="block text-xs font-medium text-neutral-600 mb-1">Bevorzugte Zeit</label>
          <select 
            id="callbackTime" 
            v-model="form.preferredTime" 
            class="w-full bg-gray-50 px-3 py-2 text-xs text-gray-700 rounded-md"
            :style="{
              border: '1px solid #e5e7eb'
            }"
          >
            <option value="morning">Vormittags (9-12 Uhr)</option>
            <option value="afternoon">Nachmittags (12-17 Uhr)</option>
            <option value="evening">Abends (17-20 Uhr)</option>
          </select>
        </div>
        
        <button 
          type="submit" 
          class="w-full px-4 py-3 text-xs font-medium rounded-md border-none mt-2"
          :class="{ 'cursor-not-allowed opacity-70': isSubmitting, 'cursor-pointer': !isSubmitting }"
          :style="{ 
            backgroundColor: styles.bubbleColor,
            color: styles.textColor || '#ffffff'
          }"
          :disabled="isSubmitting"
        >
          <span v-if="isSubmitting">Wird gesendet...</span>
          <span v-else class="flex items-center justify-center space-x-1.5">
            <AnOutlinedPhone class="h-5 w-5" />
            <span>Rückruf anfordern</span>
          </span>
        </button>
      </form>
      
      <!-- Success message -->
      <div 
        v-if="submitted" 
        class="mt-3 p-2 bg-green-50 text-green-700 text-xs font-medium rounded-md"
        :style="{
          border: '1px solid #bbf7d0'
        }"
      >
        <div class="flex items-center space-x-2">
          <div class="bg-green-500 text-white w-4 h-4 rounded-full flex justify-center items-center text-xs font-bold flex-shrink-0">
            ✓
          </div>
          <p>Vielen Dank! Wir rufen dich zurück.</p>
        </div>
      </div>
      
      <!-- Error message -->
      <div 
        v-if="errorMessage" 
        class="mt-3 p-2 bg-red-50 text-red-700 text-xs rounded-md"
        :style="{
          border: '1px solid #fecaca'
        }"
      >
        <p>{{ errorMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script>
// Import both functions for fallback mechanism
import { processCallbackRequest, sendCallbackEmails } from '../lib/appwrite';
import { AnOutlinedPhone } from '@kalimahapps/vue-icons';

export default {
  name: 'Callback',
  components: {
    AnOutlinedPhone
  },
  emits: ['callback-requested'],
  props: {
    styles: {
      type: Object,
      default: () => ({
        bubbleColor: '#3b82f6',
        textColor: '#ffffff'
      })
    }
  },
  data() {
    return {
      form: {
        name: '',
        phone: '',
        email: '',
        preferredTime: 'morning'
      },
      submitted: false,
      isSubmitting: false,
      errorMessage: ''
    };
  },
  methods: {
    async requestCallback() {
      this.isSubmitting = true;
      this.errorMessage = '';
      
      try {
        // Log the callback request
        console.log('Callback requested:', { ...this.form });
        
        // Emit the callback-requested event with form data
        this.$emit('callback-requested', { ...this.form });
        
        // First try the full processing approach
        try {
          await processCallbackRequest(this.form);
          console.log("Full callback processing successful");
        } catch (error) {
          console.error("Error in full callback processing:", error);
          
          // Fallback to just sending emails if the first approach failed
          console.log("Trying fallback - just sending emails");
          await sendCallbackEmails(this.form);
          console.log("Email-only fallback successful");
        }
        
        // Reset form
        this.form.name = '';
        this.form.phone = '';
        this.form.email = '';
        this.form.preferredTime = 'morning';
        
        // Show success message
        this.submitted = true;
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          this.submitted = false;
        }, 3000);
      } catch (error) {
        console.error('Error processing callback request:', error);
        this.errorMessage = 'Es gab ein Problem bei der Verarbeitung deiner Anfrage. Bitte versuche es später noch einmal.';
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>